<template>
  <div
    v-for="(result, index) in results"
    :key="`optimise-rx-results-${index}`"
    :class="{ 'q-mt-md': index > 0 }"
  >
    <m-banner
      :expanded="results?.length === 1 && [null, undefined, false].indexOf(expanded) === -1"
      :expand="results?.length === 1 || true === expanded"
      :label="determineOptimiseRxBannerLabel(result)"
      :type="determineOptimiseRxBannerType(result)"
      :icon="determineOptimiseRxBannerIcon(result)"
    >
      <m-layout-stack class="q-mx-md q-mb-md">
        <div v-if="'information' === determineAlternativeProductDisplayType(result)"></div>

        <!-- Alternative Product //-->
        <div
          v-else-if="
            ['single-product', 'multi-product', 'alternative-supply-quantity'].indexOf(
              determineAlternativeProductDisplayType(result),
            ) !== -1
          "
          class="detail-container"
        >
          <strong>Original drug:</strong>
          <div>{{ result.alternatives[0].product.description }}</div>
          <div>
            Price: £{{
              new Number(result.alternatives[0].costOfOriginalPrescriptionPence / 100).toFixed(2)
            }}
          </div>

          <hr class="w-full" />

          <div
            v-if="
              ['single-product', 'multi-product'].indexOf(
                determineAlternativeProductDisplayType(result),
              ) !== -1
            "
            class="detail-container"
          >
            <strong v-if="'single-product' === determineAlternativeProductDisplayType(result)"
              >Alternative drug:
            </strong>
            <strong v-else-if="'multi-product' === determineAlternativeProductDisplayType(result)"
              >Select alternative drug:
            </strong>
            <m-list>
              <m-list-item
                v-for="(alternativeProduct, idx) in result.alternatives"
                :key="`alternative-product-${index}-${idx}`"
                sr-action-label="Accept"
                sr-label="Accept"
                :no-icon="true"
                :icon-only="false"
                :on-action-click="() => acceptAlternativeProduct(result, alternativeProduct)"
              >
                {{ alternativeProduct.product.description }}
              </m-list-item>
            </m-list>
          </div>

          <!-- Alternative Supply Quantity //-->
          <div
            v-else-if="
              'alternative-supply-quantity' === determineAlternativeProductDisplayType(result)
            "
          >
            <strong>Alternative quantity:</strong>
            <m-list>
              <m-list-item
                v-for="(alternativeProduct, idx) in result.alternatives"
                :key="`alternative-product-${index}-${idx}`"
                sr-action-label="Accept"
                sr-label="Accept"
                :no-icon="true"
                :icon-only="false"
                :on-action-click="() => acceptAlternativeQuantity(result, alternativeProduct)"
              >
                {{ alternativeProduct.toSupplyQuantity }}
              </m-list-item>
            </m-list>
            <div>
              Price: £{{
                new Number(result.alternatives[0].costOfRecommendedPrescriptionPence / 100).toFixed(
                  2,
                )
              }}
            </div>
          </div>

          <m-layout-stack v-if="optimiseRxRejectionReasonIsRequired(result)">
            If you do not wish to accept this suggestion from OptimiseRx, please provide a reason
            for the medicines management team.

            <m-input
              v-model="store.state.prescription.optimiseRxRejectionReason"
              type="textarea"
              label="Reason recommendation ignored"
              required
              autogrow
            />
          </m-layout-stack>
        </div>

        <!-- Alternative Supply Quantity with different Unit of Measure //-->
        <div
          v-else-if="
            'alternative-supply-quantity-uom' === determineAlternativeProductDisplayType(result)
          "
        >
          Not handled
        </div>

        <m-layout-stack
          v-if="result.messages.section2 || result.messages.section3 || result.descriptor"
        >
          <strong>Details:</strong>
          <div v-if="result.messages.section2">{{ result.messages.section2 }}</div>
          <div v-if="result.messages.section3">{{ result.messages.section3 }}</div>
          <div v-if="result.descriptor">Rationale: {{ result.descriptor }}</div>
        </m-layout-stack>

        <hr v-if="result.referenceUrls?.length > 0" class="w-full" />
        <div v-if="result.referenceUrls?.length > 0">
          <strong>References:</strong>
          <ul>
            <li v-for="(referenceUrl, idx) in result.referenceUrls" :key="`reference-url-${idx}`">
              <m-link :href="referenceUrl.url">{{ referenceUrl.description }}</m-link>
            </li>
          </ul>
        </div>
      </m-layout-stack>
    </m-banner>
  </div>

  <!-- {{ results }} -->
</template>
<script lang="ts" setup>
import MList from "../../../medicus/MList";
import {
  determineOptimiseRxResultHasMetaDataType,
  determineOptimiseRxBannerIcon,
  determineOptimiseRxBannerType,
} from "./optimise-rx";
import { usePrescriptionStore } from "../../../../store/transitient/prescription";
import { acceptOptimiseRxProduct } from "../../../../store/transitient/prescription/api";
import { onMounted } from "vue";

const store = usePrescriptionStore();

const props = defineProps<{
  results: Array;
  expanded?: boolean;
}>();

onMounted(() => {
  store.state.prescription.optimiseRxUserId = store.state.optimiseRxUserId;
  store.state.prescription.optimiseRxRuleId = props.results[0]?.ruleId;
});

const optimiseRxRejectionReasonIsRequired = (result) => {
  /**
   * @todo [Jack] ENG-16951 This needs to be conditional for:
   * Complex, Focus: Best Practice, Intent: Prescribe alternative
   * Focus: Rx Budget, Intent: Prescribe alternative
   */
  // return true;

  if (
    result.isComplex &&
    determineOptimiseRxResultHasMetaDataType(result, "Best Practice") &&
    determineOptimiseRxResultHasMetaDataType(result, "Prescribe alternative")
  ) {
    return true;
  }

  if (
    determineOptimiseRxResultHasMetaDataType(result, "Rx Budget") &&
    determineOptimiseRxResultHasMetaDataType(result, "Prescribe alternative")
  ) {
    return true;
  }

  return false;
};

const acceptAlternativeProduct = async (result, alternativeProduct) => {
  store.skipOptimiseRxSync = true;

  await acceptOptimiseRxProduct(store.state.optimiseRxUserId, result.ruleId).then(() => {
    if (alternativeProduct.toSupplyQuantity) {
      store.preservedIssueQuantity = alternativeProduct.toSupplyQuantity;
    }

    store.drugOrDevice = {
      itemCode: alternativeProduct.product.conceptId,
      description: alternativeProduct.product.description,
    };
  });
};

const acceptAlternativeQuantity = (result, alternativeProduct) => {
  store.skipOptimiseRxSync = true;
  store.state.prescription.issueQuantity.value = alternativeProduct.toSupplyQuantity;
  store.state.prescription.optimiseRxRuleId = null;
  store.state.prescription.optimiseRxRejectionReason = null;
};

const determineAlternativeProductDisplayType = (result) => {
  if (result.alternatives.length === 0) {
    return "information";
  }

  if (result.alternatives.length === 1) {
    if (result.alternatives[0].toSupplyQuantity) {
      return "alternative-supply-quantity";
      // return "single-supply-quantity";
    }

    return "single-product";
  }

  if (result.alternatives[0].product.conceptId) {
    return "multi-product";
  }

  if (result.alternatives[0].toSupplyQuantity) {
    return "alternative-supply-quantity";
    // return "multi-supply-quantity";
  }

  return "unknown";
};

const determineOptimiseRxBannerLabel = (result) => {
  const type = determineAlternativeProductDisplayType(result);

  if ("information" === type) {
    return result.messages.section1 ?? result.title;
  }

  if ("single-product" === type || "alternative-supply-quantity" === type) {
    return `${result.messages.section1} · Saving ${result.costSavingUnitOfMeasure}${result.costSavingType} per acute`;
  }

  if ("multi-product" === type) {
    return `${result.messages.section1} · Average saving ${result.costSavingUnitOfMeasure}${result.costSavingType} per acute`;
  }
};
</script>
<style>
.detail-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
</style>
